import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { FiExternalLink } from 'react-icons/fi'
import { FaRegTrashAlt } from 'react-icons/fa'
import AdmiSubHeader from '../../components/subheader/admi-subheader.components'
import { useOrder } from '../../hooks/useAuth'
import {
  errorToast,
  imageDb,
  infoToast,
} from '../../utils/firebase/firebase.utils'
import { AdmiMenu } from './admi-menu.components'
import './admi.styles.scss'
import './pages.styles.scss'

const GALLERY_CATEGORIES = [
  'Cupcake',
  'Birthday Cake',
  'Anniversary Cake',
  'Celebration Cake',
  'Wedding Cake',
  'Pastries',
  'Custom',
]

const defaultCompanyValues = () => ({
  companyDisplayName: '',
  companyName: '',
  slogan: '',
  address1: '',
  address2: '',
  phone: '',
  email: '',
  country: '',
  ig: '',
  fb: '',
  tw: '',
  tq: '',
  yt: '',
  logo: '',
  createdAt: new Date(),
})

const defaultPageValues = () => ({
  title: 'Deliciously Baked, Lovingly Made',
  subtitle1: 'Handcrafted cakes and pastries that bring joy to every occasion,',
  subtitle2: 'made with the finest ingredients and a touch of love.',
  cat1: 'Wedding Cakes',
  cat1_desc: 'Elegant tiers and custom designs for your celebration.',
  cat1_img: '',
  cat2: 'Birthday Cakes',
  cat2_desc: 'Festive flavors sized for every party and guest list.',
  cat2_img: '',
  cat3: 'Custom Cakes',
  cat3_desc: 'Tell us your vision. We bake it to order.',
  cat3_img: '',
  featured_img_1: '',
  featured_img_2: '',
  featured_img_3: '',
  about_img_url: '',
  about_name: 'Sandy',
  about_title: 'Baker & founder',
  about_text:
    "From decadent cakes to mouthwatering pastries, every creation is crafted with passion and care.",
  more_sandy:
    "From decadent cakes to mouthwatering pastries, every creation is crafted with passion. Explore the gallery, book your custom order, and enjoy baked goods made with love.",
  del: 'no',
  company: defaultCompanyValues(),
  created_at: new Date(),
  updated_at: null,
})

const nextFileId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const SECTIONS = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'gallery', label: 'Gallery' },
]

const collectDocUrls = (item) => {
  if (!item || item.del === 'yes') return []
  if (Array.isArray(item.urls)) {
    return item.urls.filter((url) => typeof url === 'string' && url.trim())
  }
  if (typeof item.url === 'string' && item.url.trim()) return [item.url.trim()]
  if (typeof item.imageLink === 'string' && item.imageLink.trim()) {
    return [item.imageLink.trim()]
  }
  if (typeof item.img === 'string' && item.img.trim()) return [item.img.trim()]
  return []
}

const Field = ({ label, hint, children }) => (
  <label className="pages-v2__field">
    <span className="pages-v2__label">
      {label}
      {hint ? <em>{hint}</em> : null}
    </span>
    {children}
  </label>
)

const ImageField = ({
  label,
  value,
  onUrlChange,
  onUploaded,
  uploading,
  disabled,
}) => (
  <div className="pages-v2__image-field">
    <span className="pages-v2__label">{label}</span>
    <div className="pages-v2__image-row">
      <div className="pages-v2__image-preview">
        {value ? <img src={value} alt="" /> : <span>No image</span>}
      </div>
      <div className="pages-v2__image-controls">
        <input
          type="url"
          value={value || ''}
          onChange={(event) => onUrlChange(event.target.value)}
          placeholder="https://… or upload a file"
        />
        <label className="pages-v2__upload">
          <input
            type="file"
            accept="image/*"
            disabled={disabled || uploading}
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) onUploaded(file)
            }}
          />
          {uploading ? 'Uploading…' : 'Upload image'}
        </label>
      </div>
    </div>
  </div>
)

const PagesEdit = () => {
  const {
    curPage,
    addPage,
    getCurPage,
    addToGal,
    gallery,
    getGallery,
    updateGallery,
  } = useOrder()
  // companyFields kept in sync with curPage so Home/About saves do not wipe Settings company data
  const [section, setSection] = useState('home')
  const [formFields, setFormFields] = useState(defaultPageValues)
  const [companyFields, setCompanyFields] = useState(defaultCompanyValues)
  const [saving, setSaving] = useState(false)
  const [uploadingKey, setUploadingKey] = useState('')
  const [galleryCat, setGalleryCat] = useState('')
  const [galleryFiles, setGalleryFiles] = useState([])
  const [galleryBusy, setGalleryBusy] = useState(false)
  const [galleryFilter, setGalleryFilter] = useState('all')
  const [busyImageKey, setBusyImageKey] = useState('')

  useEffect(() => {
    if (typeof getCurPage === 'function') getCurPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!curPage) return

    const company =
      curPage.company && !Array.isArray(curPage.company)
        ? { ...defaultCompanyValues(), ...curPage.company }
        : defaultCompanyValues()

    setFormFields({
      ...defaultPageValues(),
      ...curPage,
      company,
    })
    setCompanyFields(company)
  }, [curPage])

  const setPageField = (name, value) => {
    setFormFields((prev) => ({ ...prev, [name]: value }))
  }

  const setCompanyField = (name, value) => {
    setCompanyFields((prev) => ({ ...prev, [name]: value }))
  }

  const galleryImages = useMemo(() => {
    const list = Array.isArray(gallery) ? gallery : []
    const items = []

    list.forEach((docItem) => {
      if (!docItem?.id || docItem.del === 'yes') return
      const urls = collectDocUrls(docItem)
      urls.forEach((url, index) => {
        items.push({
          key: `${docItem.id}::${index}::${url}`,
          docId: docItem.id,
          url,
          cat: docItem.cat || 'Custom',
          index,
        })
      })
    })

    return items
  }, [gallery])

  const filteredGalleryImages = useMemo(() => {
    if (galleryFilter === 'all') return galleryImages
    return galleryImages.filter((item) => item.cat === galleryFilter)
  }, [galleryImages, galleryFilter])

  const galleryCounts = useMemo(() => {
    const counts = { all: galleryImages.length }
    GALLERY_CATEGORIES.forEach((cat) => {
      counts[cat] = galleryImages.filter((item) => item.cat === cat).length
    })
    return counts
  }, [galleryImages])

  const uploadImage = async (file, folder = 'files') => {
    const imgRef = ref(imageDb, `${folder}/${nextFileId()}-${file.name}`)
    const snapshot = await uploadBytes(imgRef, file)
    return getDownloadURL(snapshot.ref)
  }

  const handleImageUpload = async (file, fieldName, scope = 'page') => {
    if (!file) return
    setUploadingKey(fieldName)
    try {
      const url = await uploadImage(file, 'files')
      if (scope === 'company') {
        setCompanyField(fieldName, url)
      } else {
        setPageField(fieldName, url)
      }
      infoToast('Image uploaded. Save this section to publish it.')
    } catch (error) {
      console.error('Image upload failed:', error)
      errorToast('Could not upload image. Please try again.')
    } finally {
      setUploadingKey('')
    }
  }

  const savePage = async (message) => {
    if (saving) return
    const ok = window.confirm('Save these page updates?')
    if (!ok) return

    setSaving(true)
    try {
      const { id: _ignoredId, ...payload } = {
        ...formFields,
        company: companyFields,
        hours: curPage?.hours || formFields.hours || null,
        del: 'no',
      }
      await addPage(payload, message)
    } catch (error) {
      console.error('Save page failed:', error)
      errorToast('Could not save page updates. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const saveGallery = async () => {
    if (!galleryCat) {
      return infoToast('Select a gallery category first.')
    }
    if (galleryFiles.length === 0) {
      return infoToast('Select one or more images to upload.')
    }

    setGalleryBusy(true)
    try {
      const urls = await Promise.all(
        galleryFiles.map((file) => uploadImage(file, 'gallery'))
      )
      await addToGal({
        cat: galleryCat,
        urls,
        del: 'no',
        created_at: new Date(),
        updated_at: null,
      })
      setGalleryFiles([])
      setGalleryCat('')
    } catch (error) {
      console.error('Gallery upload failed:', error)
      errorToast('Could not upload gallery images. Please try again.')
    } finally {
      setGalleryBusy(false)
    }
  }

  const findGalleryDoc = (docId) =>
    (Array.isArray(gallery) ? gallery : []).find((item) => item.id === docId)

  const removeUrlFromDoc = async (docItem, urlToRemove, successMsg = 'Image deleted.') => {
    const urls = collectDocUrls(docItem).filter((url) => url !== urlToRemove)

    if (urls.length === 0) {
      await updateGallery(
        {
          ...docItem,
          urls: [],
          del: 'yes',
        },
        successMsg
      )
      return
    }

    await updateGallery(
      {
        ...docItem,
        urls,
        del: 'no',
      },
      successMsg
    )
  }

  const deleteGalleryImage = async (image) => {
    const docItem = findGalleryDoc(image.docId)
    if (!docItem) {
      return infoToast('Could not find that gallery image.')
    }

    const ok = window.confirm('Delete this gallery image?')
    if (!ok) return

    setBusyImageKey(image.key)
    try {
      await removeUrlFromDoc(docItem, image.url, 'Image deleted.')
    } catch (error) {
      console.error('Delete gallery image failed:', error)
      errorToast('Could not delete image. Please try again.')
    } finally {
      setBusyImageKey('')
    }
  }

  const changeGalleryCategory = async (image, nextCat) => {
    if (!nextCat || nextCat === image.cat) return

    const sourceDoc = findGalleryDoc(image.docId)
    if (!sourceDoc) {
      return infoToast('Could not find that gallery image.')
    }

    const targetDoc = (Array.isArray(gallery) ? gallery : []).find(
      (item) =>
        item.id !== image.docId &&
        item.del !== 'yes' &&
        item.cat === nextCat
    )

    setBusyImageKey(image.key)
    try {
      const remaining = collectDocUrls(sourceDoc).filter((url) => url !== image.url)

      if (remaining.length === 0) {
        await updateGallery(
          {
            ...sourceDoc,
            urls: [],
            del: 'yes',
          },
          null
        )
      } else {
        await updateGallery(
          {
            ...sourceDoc,
            urls: remaining,
            del: 'no',
          },
          null
        )
      }

      if (targetDoc) {
        await updateGallery(
          {
            ...targetDoc,
            urls: [...collectDocUrls(targetDoc), image.url],
            del: 'no',
          },
          'Category updated.'
        )
      } else {
        await addToGal(
          {
            cat: nextCat,
            urls: [image.url],
            del: 'no',
            created_at: new Date(),
            updated_at: null,
          },
          'Category updated.'
        )
      }
    } catch (error) {
      console.error('Change gallery category failed:', error)
      errorToast('Could not change category. Please try again.')
      if (typeof getGallery === 'function') await getGallery()
    } finally {
      setBusyImageKey('')
    }
  }

  return (
    <div className="admin-shell">
      <AdmiSubHeader h2="Pages" h6="Edit homepage, about, and gallery content" />
      <AdmiMenu />

      <div className="admin-shell__body pages-v2">
        <div className="pages-v2__tabs" role="tablist" aria-label="Page sections">
          {SECTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={section === item.id}
              className={section === item.id ? 'is-active' : ''}
              onClick={() => setSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {section === 'home' ? (
          <section className="pages-v2__panel">
            <div className="pages-v2__preview">
              <p>Shows on the public homepage hero, categories, and featured copy.</p>
              <Link to="/" target="_blank" rel="noreferrer">
                Preview home <FiExternalLink size={13} aria-hidden="true" />
              </Link>
            </div>

            <div className="pages-v2__grid">
              <Field label="Hero title" hint="max 40">
                <input
                  type="text"
                  name="title"
                  maxLength={40}
                  value={formFields.title || ''}
                  onChange={(event) => setPageField('title', event.target.value)}
                />
              </Field>
              <Field label="Subtitle 1" hint="max 65">
                <input
                  type="text"
                  name="subtitle1"
                  maxLength={65}
                  value={formFields.subtitle1 || ''}
                  onChange={(event) => setPageField('subtitle1', event.target.value)}
                />
              </Field>
              <Field label="Subtitle 2" hint="max 55">
                <input
                  type="text"
                  name="subtitle2"
                  maxLength={55}
                  value={formFields.subtitle2 || ''}
                  onChange={(event) => setPageField('subtitle2', event.target.value)}
                />
              </Field>
            </div>

            <h3 className="pages-v2__section-title">Categories</h3>
            {[1, 2, 3].map((n) => (
              <div key={n} className="pages-v2__card-block">
                <p className="pages-v2__block-label">Category {n}</p>
                <div className="pages-v2__grid">
                  <Field label="Name" hint="max 24">
                    <input
                      type="text"
                      maxLength={24}
                      value={formFields[`cat${n}`] || ''}
                      onChange={(event) => setPageField(`cat${n}`, event.target.value)}
                    />
                  </Field>
                  <Field label="Description" hint="max 150">
                    <textarea
                      rows={3}
                      maxLength={150}
                      value={formFields[`cat${n}_desc`] || ''}
                      onChange={(event) => setPageField(`cat${n}_desc`, event.target.value)}
                    />
                  </Field>
                </div>
                <ImageField
                  label={`Category ${n} image`}
                  value={formFields[`cat${n}_img`] || ''}
                  onUrlChange={(value) => setPageField(`cat${n}_img`, value)}
                  onUploaded={(file) => handleImageUpload(file, `cat${n}_img`)}
                  uploading={uploadingKey === `cat${n}_img`}
                  disabled={saving}
                />
              </div>
            ))}

            <h3 className="pages-v2__section-title">Featured</h3>
            <Field label="More from Sandy" hint="max 390">
              <textarea
                rows={5}
                maxLength={390}
                value={formFields.more_sandy || ''}
                onChange={(event) => setPageField('more_sandy', event.target.value)}
              />
            </Field>

            <div className="pages-v2__grid pages-v2__grid--images">
              {[1, 2, 3].map((n) => (
                <ImageField
                  key={n}
                  label={`Featured image ${n}`}
                  value={formFields[`featured_img_${n}`] || ''}
                  onUrlChange={(value) => setPageField(`featured_img_${n}`, value)}
                  onUploaded={(file) => handleImageUpload(file, `featured_img_${n}`)}
                  uploading={uploadingKey === `featured_img_${n}`}
                  disabled={saving}
                />
              ))}
            </div>

            <div className="pages-v2__actions">
              <button
                type="button"
                className="pages-v2__save"
                disabled={saving || Boolean(uploadingKey)}
                onClick={() => savePage('Homepage updated.')}
              >
                {saving ? 'Saving…' : 'Save homepage'}
              </button>
            </div>
          </section>
        ) : null}

        {section === 'about' ? (
          <section className="pages-v2__panel">
            <div className="pages-v2__preview">
              <p>Shows on the About page portrait and story text.</p>
              <Link to="/about" target="_blank" rel="noreferrer">
                Preview about <FiExternalLink size={13} aria-hidden="true" />
              </Link>
            </div>

            <ImageField
              label="About image"
              value={formFields.about_img_url || ''}
              onUrlChange={(value) => setPageField('about_img_url', value)}
              onUploaded={(file) => handleImageUpload(file, 'about_img_url')}
              uploading={uploadingKey === 'about_img_url'}
              disabled={saving}
            />

            <div className="pages-v2__grid">
              <Field label="Name" hint="max 20">
                <input
                  type="text"
                  maxLength={20}
                  value={formFields.about_name || ''}
                  onChange={(event) => setPageField('about_name', event.target.value)}
                />
              </Field>
              <Field label="Title / role" hint="max 24">
                <input
                  type="text"
                  maxLength={24}
                  value={formFields.about_title || ''}
                  onChange={(event) => setPageField('about_title', event.target.value)}
                />
              </Field>
            </div>

            <Field label="About text">
              <textarea
                rows={8}
                value={formFields.about_text || ''}
                onChange={(event) => setPageField('about_text', event.target.value)}
              />
            </Field>

            <div className="pages-v2__actions">
              <button
                type="button"
                className="pages-v2__save"
                disabled={saving || Boolean(uploadingKey)}
                onClick={() => savePage('About page updated.')}
              >
                {saving ? 'Saving…' : 'Save about'}
              </button>
            </div>
          </section>
        ) : null}

        {section === 'gallery' ? (
          <section className="pages-v2__panel">
            <div className="pages-v2__preview">
              <p>Upload images, then manage category or delete below.</p>
              <Link to="/gallery" target="_blank" rel="noreferrer">
                Preview gallery <FiExternalLink size={13} aria-hidden="true" />
              </Link>
            </div>

            <div className="pages-v2__grid">
              <Field label="Category">
                <select
                  value={galleryCat}
                  onChange={(event) => setGalleryCat(event.target.value)}
                >
                  <option value="">Select category</option>
                  {GALLERY_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Images">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => {
                    setGalleryFiles(Array.from(event.target.files || []))
                  }}
                />
              </Field>
            </div>

            {galleryFiles.length > 0 ? (
              <p className="pages-v2__file-count">
                {galleryFiles.length} file{galleryFiles.length === 1 ? '' : 's'} selected
              </p>
            ) : null}

            <div className="pages-v2__actions">
              <button
                type="button"
                className="pages-v2__save"
                disabled={galleryBusy}
                onClick={saveGallery}
              >
                {galleryBusy ? 'Uploading…' : 'Upload to gallery'}
              </button>
            </div>

            <h3 className="pages-v2__section-title">Manage images</h3>

            <div className="admin-toolbar__filters pages-v2__gallery-filters" role="group" aria-label="Filter gallery images">
              <button
                type="button"
                className={galleryFilter === 'all' ? 'is-active' : ''}
                onClick={() => setGalleryFilter('all')}
              >
                All ({galleryCounts.all})
              </button>
              {GALLERY_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={galleryFilter === cat ? 'is-active' : ''}
                  onClick={() => setGalleryFilter(cat)}
                >
                  {cat} ({galleryCounts[cat] || 0})
                </button>
              ))}
            </div>

            {filteredGalleryImages.length === 0 ? (
              <p className="pages-v2__empty-gallery">
                {galleryCounts.all === 0
                  ? 'No gallery images yet. Upload some above.'
                  : 'No images in this category.'}
              </p>
            ) : (
              <ul className="pages-v2__gallery-list">
                {filteredGalleryImages.map((image) => {
                  const busy = busyImageKey === image.key
                  return (
                    <li key={image.key} className="pages-v2__gallery-item">
                      <div className="pages-v2__gallery-thumb">
                        <img src={image.url} alt="" />
                      </div>
                      <div className="pages-v2__gallery-meta">
                        <label className="pages-v2__field">
                          <span className="pages-v2__label">Category</span>
                          <select
                            value={image.cat}
                            disabled={busy || Boolean(busyImageKey)}
                            onChange={(event) =>
                              changeGalleryCategory(image, event.target.value)
                            }
                          >
                            {GALLERY_CATEGORIES.map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          className="pages-v2__gallery-delete"
                          disabled={busy || Boolean(busyImageKey)}
                          onClick={() => deleteGalleryImage(image)}
                        >
                          <FaRegTrashAlt size={12} aria-hidden="true" />
                          {busy ? 'Working…' : 'Delete'}
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        ) : null}
      </div>
    </div>
  )
}

export default PagesEdit
